/* eslint-disable @angular-eslint/no-output-native */
import { ChangeDetectorRef, Directive, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { DynamicFormControlComponent, DynamicFormControlCustomEvent, DynamicFormControlLayout, DynamicFormLayoutService, DynamicFormValidationService, DynamicFormValueControlModel } from '@ng-dynamic-forms/core';
import { DynamicFormControlModel } from '@ng-dynamic-forms/core/lib/model/dynamic-form-control.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PropType } from '../helpers/prop-type.helper';

@UntilDestroy()
@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class CustomDynamicFormControlComponent<ModelT extends DynamicFormControlModel> extends DynamicFormControlComponent
  implements OnInit {
  protected layoutService: DynamicFormLayoutService;
  protected validationService: DynamicFormValidationService;
  protected cd = inject(ChangeDetectorRef);

  private _inited = false;
  private _model: ModelT;
  protected isValueChanging = false;

  // important: model should not be overridden, otherwise setter won't work!
  @Input()
  // @ts-ignore
  set model(model: ModelT) {
    this._model = model;
    this.modelChanges.next(model);
    if (this._inited) {
      // call onModelChange() before subscribing to value/disabled changes, in case contents of model need adjustment
      this.onModelChange();
      this._subscribeToChanges();
    }
  }

  get model(): ModelT {
    return this._model;
  }

  @Input() group: UntypedFormGroup;
  @Input() layout: DynamicFormControlLayout;

  @Output() blur = new EventEmitter<any>();
  @Output() change = new EventEmitter<ModelT extends { value: any } ? PropType<ModelT, 'value'> : any>();
  @Output() focus = new EventEmitter<any>();
  @Output() customEvent = new EventEmitter<DynamicFormControlCustomEvent>();

  protected modelChanges = new Subject<ModelT>();
  protected untilNextModel = takeUntil(this.modelChanges.pipe(untilDestroyed(this)));

  protected constructor() {
    const layoutService = inject(DynamicFormLayoutService);
    const validationService = inject(DynamicFormValidationService);

    super(layoutService, validationService);

    this.layoutService = layoutService;
    this.validationService = validationService;
  }

  // !! Note: if overridden it's important to call super.ngOnInit() as the first statement.
  ngOnInit() {
    if (this.model) {
      // call onModelChange() before subscribing to value/disabled changes, in case contents of model need adjustment
      this.onModelChange();
      this._subscribeToChanges();
    }
    this._inited = true;
  }

  private _subscribeToChanges() {
    if (this.model instanceof DynamicFormValueControlModel && this.onValueChange) {
      this.model.valueChanges.pipe(this.untilNextModel)
        .subscribe(() => {
          // prevent potential feedback loop from value changing within onValueChange()
          if (!this.isValueChanging) {
            this.isValueChanging = true;
            this.onValueChange();
            this.isValueChanging = false;
          }
        });
    }
    this.model.disabledChanges.pipe(this.untilNextModel)
      .subscribe(() => {
        this.onDisabledChange();
        // run change detection after any changes have been made
        this.cd.detectChanges();
      });
  }

  // overridable, do nothing by default
  protected onModelChange(): void {
  }

  // overridable, do nothing by default
  protected onDisabledChange(): void {
  }

  // optionally implementable
  protected onValueChange?(): void;
}
